import React, { useState, useRef, useEffect, useCallback } from "react";
import {
  TextField,
  InputAdornment,
  IconButton,
  Paper,
  CircularProgress,
  Typography,
  ClickAwayListener,
  Chip,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import { apiPaths, get, postData } from "../api-integration-util";
import QuestionViewModal from "../../question-bank/questionViewmodal";

export default function GlobalQuestionSearch() {
  const [searchText, setSearchText] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [noResults, setNoResults] = useState(false);

  // Question view modal state
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [questionData, setQuestionData] = useState({});
  const [openModal, setOpenModal] = useState(false);

  const debounceTimer = useRef(null);
  const inputRef = useRef(null);

  const isNumeric = (val) => /^\d+$/.test(val.trim());

  const searchById = useCallback((id) => {
    setLoading(true);
    setShowDropdown(true);
    setNoResults(false);
    setResults([]);
    get(apiPaths.questionbyid, { id: +id })
      .then(({ result }) => {
        if (result && result.questionId) {
          setResults([
            {
              questionId: result.questionId,
              questionPlainText:
                result.question?.replace(/<[^>]*>/g, "")?.substring(0, 120) ||
                "Question #" + result.questionId,
              subject: result.subject,
              questionStatus: result.questionStatus,
            },
          ]);
          setNoResults(false);
        } else {
          setResults([]);
          setNoResults(true);
        }
      })
      .catch(() => {
        setResults([]);
        setNoResults(true);
      })
      .finally(() => setLoading(false));
  }, []);

  const searchByText = useCallback((text) => {
    setLoading(true);
    setShowDropdown(true);
    setNoResults(false);
    setResults([]);
    postData(apiPaths.questionslistPaginated, {
      data: {
        statuses: ["APPROVED"],
        page: 0,
        pageSize: 10,
        sortBy: "question_id",
        sortOrder: "DESC",
        searchText: text.trim(),
      },
    })
      .then((res) => {
        const data = res?.result?.data || res?.result || [];
        const list = Array.isArray(data) ? data : [];
        setResults(list.slice(0, 10));
        setNoResults(list.length === 0);
      })
      .catch(() => {
        setResults([]);
        setNoResults(true);
      })
      .finally(() => setLoading(false));
  }, []);

  const handleInputChange = (e) => {
    const value = e.target.value;
    setSearchText(value);

    if (debounceTimer.current) clearTimeout(debounceTimer.current);

    if (!value.trim()) {
      setResults([]);
      setShowDropdown(false);
      setNoResults(false);
      return;
    }

    // For numeric input, search immediately on Enter only (handled in onKeyDown)
    // For text input, debounce
    if (!isNumeric(value)) {
      debounceTimer.current = setTimeout(() => {
        if (value.trim().length >= 3) {
          searchByText(value);
        }
      }, 500);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && searchText.trim()) {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
      if (isNumeric(searchText)) {
        searchById(searchText.trim());
      } else if (searchText.trim().length >= 2) {
        searchByText(searchText);
      }
    }
    if (e.key === "Escape") {
      handleClose();
    }
  };

  const handleSearchClick = () => {
    if (!searchText.trim()) return;
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    if (isNumeric(searchText)) {
      searchById(searchText.trim());
    } else if (searchText.trim().length >= 2) {
      searchByText(searchText);
    }
  };

  const handleResultClick = (questionId) => {
    setShowDropdown(false);
    setLoading(true);
    get(apiPaths.questionbyid, { id: questionId })
      .then(({ result }) => {
        setQuestionData(result);
        setSelectedQuestion(questionId);
        setOpenModal(true);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedQuestion(null);
    setQuestionData({});
  };

  const handleClose = () => {
    setShowDropdown(false);
  };

  const handleClear = () => {
    setSearchText("");
    setResults([]);
    setShowDropdown(false);
    setNoResults(false);
    if (inputRef.current) inputRef.current.focus();
  };

  useEffect(() => {
    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    };
  }, []);

  // When navigating questions via prev/next in the modal, refetch data
  const handleSetId = (newId) => {
    setSelectedQuestion(newId);
    get(apiPaths.questionbyid, { id: newId })
      .then(({ result }) => {
        setQuestionData(result);
      })
      .catch(() => setQuestionData({}));
  };

  return (
    <div className="global-question-search">
      <ClickAwayListener onClickAway={handleClose}>
        <div className="global-question-search__wrapper">
          <TextField
            inputRef={inputRef}
            size="small"
            placeholder="Search by Question ID or text..."
            value={searchText}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onFocus={() => {
              if (results.length > 0 || noResults) setShowDropdown(true);
            }}
            className="global-question-search__input"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon
                    fontSize="small"
                    style={{ color: "#888", cursor: "pointer" }}
                    onClick={handleSearchClick}
                  />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  {loading ? (
                    <CircularProgress size={18} />
                  ) : searchText ? (
                    <IconButton size="small" onClick={handleClear}>
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  ) : null}
                </InputAdornment>
              ),
            }}
          />

          {showDropdown && (
            <Paper className="global-question-search__dropdown" elevation={4}>
              {loading && results.length === 0 && (
                <div className="global-question-search__loading">
                  <CircularProgress size={24} />
                  <Typography variant="body2" style={{ marginLeft: 8 }}>
                    Searching...
                  </Typography>
                </div>
              )}

              {noResults && !loading && (
                <div className="global-question-search__no-results">
                  <Typography variant="body2" color="textSecondary">
                    No questions found
                  </Typography>
                </div>
              )}

              {results.map((item) => (
                <div
                  key={item.questionId}
                  className="global-question-search__result-item"
                  onClick={() => handleResultClick(item.questionId)}
                >
                  <div className="global-question-search__result-header">
                    <Typography
                      variant="body2"
                      className="global-question-search__result-id"
                    >
                      #{item.questionId}
                    </Typography>
                    <div className="global-question-search__result-chips">
                      {item.subject && (
                        <Chip
                          label={item.subject}
                          size="small"
                          color="secondary"
                          variant="outlined"
                        />
                      )}
                      {item.questionStatus && (
                        <Chip
                          label={item.questionStatus}
                          size="small"
                          color={
                            item.questionStatus === "APPROVED"
                              ? "success"
                              : "default"
                          }
                          variant="outlined"
                        />
                      )}
                    </div>
                  </div>
                  <Typography
                    variant="caption"
                    color="textSecondary"
                    className="global-question-search__result-text"
                  >
                    {item.questionPlainText?.substring(0, 120) || "—"}
                    {(item.questionPlainText?.length || 0) > 120 ? "..." : ""}
                  </Typography>
                </div>
              ))}

              {!loading && results.length > 0 && (
                <div className="global-question-search__hint">
                  <Typography variant="caption" color="textSecondary">
                    Click a result to view the full question
                  </Typography>
                </div>
              )}
            </Paper>
          )}
        </div>
      </ClickAwayListener>

      {selectedQuestion && (
        <QuestionViewModal
          open={openModal}
          handleClose={handleCloseModal}
          questionData={questionData}
          selectedQuestion={selectedQuestion}
          prevQuestion={null}
          nxtQuestion={null}
          QBCount={0}
          setId={handleSetId}
        />
      )}
    </div>
  );
}
